import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from './api.js'

/**
 * Query keys and mutation hooks (D6).
 *
 * ── WHY INVALIDATION LIVES HERE AND NOT IN COMPONENTS ───────────────────────
 * One core-loop action almost always changes several screens at once. Moving a
 * job to a new stage changes the board, the job's own detail, and its stage
 * history. Accepting an estimate changes the estimate, the job it hangs off,
 * and whether an invoice can be created at all.
 *
 * If each component invalidated its own idea of what it just affected, the
 * misses would not look like bugs — they would look like the product being
 * slow or flaky. "I moved the card and the board didn't update" is the classic
 * report, and it is almost never a caching problem; it is a forgotten
 * invalidation.
 *
 * So every mutation is defined once, here, next to the exact list of keys it
 * invalidates. A component calls the hook and cannot forget.
 *
 * ── KEY SHAPE ───────────────────────────────────────────────────────────────
 * Hierarchical, so a broad invalidation catches every narrower one. Passing
 * `keys.jobs()` to invalidateQueries matches ['jobs'] AND ['jobs', {stage_id}]
 * — meaning a filtered board view refreshes without anyone enumerating filters.
 */

export const keys = {
  me: () => ['me'],
  stages: () => ['pipeline-stages'],
  users: () => ['users'],
  userSeats: () => ['users', 'seats'],
  formKeys: () => ['form-keys'],

  jobs: filters => (filters ? ['jobs', filters] : ['jobs']),
  job: id => ['job', id],

  clients: params => (params ? ['clients', params] : ['clients']),
  client: id => ['client', id],

  estimates: filters => (filters ? ['estimates', filters] : ['estimates']),
  estimate: id => ['estimate', id],
  shareLinks: estimateId => ['estimate', estimateId, 'share-links'],

  settings: () => ['settings'],
  onboarding: () => ['payments-onboarding'],

  invoices: filters => (filters ? ['invoices', filters] : ['invoices']),
  invoice: id => ['invoice', id],
  invoicePayments: id => ['invoice', id, 'payments'],
  invoiceAmendments: id => ['invoice', id, 'amendments'],
  balanceInvoice: jobId => ['job', jobId, 'balance-invoice'],

  appointments: params => (params ? ['appointments', params] : ['appointments']),

  estimateDeliveries: estimateId => ['estimate', estimateId, 'deliveries'],
  invoiceDeliveries: invoiceId => ['invoice', invoiceId, 'deliveries'],
  sendingDomain: () => ['sending-domain'],

  notifications: () => ['notifications'],
  notificationPreferences: () => ['notification-preferences'],

  dataExports: () => ['data-exports'],

  operationsOverview: () => ['operations', 'overview'],
  operationsReport: filters => ['operations', 'reports', filters],
  documentTemplates: () => ['operations', 'templates']
}

/* ==========================================================================
   Reads
   ========================================================================== */

export const useStages = () => useQuery({
  queryKey: keys.stages(),
  queryFn: api.getPipelineStages,
  // Stages are tenant configuration and change roughly never. Refetching them
  // on every board mount is pure waste.
  staleTime: 10 * 60 * 1000,
  select: data => data.pipeline_stages
})

export const useUsers = () => useQuery({
  queryKey: keys.users(),
  queryFn: api.getUsers,
  staleTime: 5 * 60 * 1000,
  select: data => data.users
})

export const useUserSeats = () => useQuery({
  queryKey: keys.userSeats(),
  queryFn: api.getUserSeats,
  staleTime: 5 * 60 * 1000,
  select: data => data.seats
})

export const useJobs = (filters = {}) => useQuery({
  queryKey: keys.jobs(filters),
  queryFn: () => api.getJobs(filters),
  select: data => data.jobs
})

export const useJob = id => useQuery({
  queryKey: keys.job(id),
  queryFn: () => api.getJob(id),
  enabled: Boolean(id)
})

export const useClients = (params = {}) => useQuery({
  queryKey: keys.clients(params),
  queryFn: () => api.getClients(params),
  select: data => data.clients
})

export const useClient = id => useQuery({
  queryKey: keys.client(id),
  queryFn: () => api.getClient(id),
  enabled: Boolean(id)
})

export const useEstimate = id => useQuery({
  queryKey: keys.estimate(id),
  queryFn: () => api.getEstimate(id),
  enabled: Boolean(id),
  select: data => data.estimate
})

/**
 * The signed document's metadata, from the SAME request `useEstimate` makes.
 *
 * Same query key, different `select`. TanStack dedupes on the key, so a screen
 * calling both hooks issues ONE fetch — which is the reason signature status is
 * embedded on GET /api/estimates/:id rather than given its own endpoint.
 */
export const useEstimateSignature = id => useQuery({
  queryKey: keys.estimate(id),
  queryFn: () => api.getEstimate(id),
  enabled: Boolean(id),
  select: data => data.signature ?? null
})

export const useInvoice = id => useQuery({
  queryKey: keys.invoice(id),
  queryFn: () => api.getInvoice(id),
  enabled: Boolean(id),
  select: data => data.invoice
})

export const useInvoices = (filters = {}) => useQuery({
  queryKey: keys.invoices(filters),
  queryFn: () => api.getInvoices(filters),
  select: data => data.invoices
})

export const useAppointments = (params = {}) => useQuery({
  queryKey: keys.appointments(params),
  queryFn: () => api.getAppointments(params),
  select: data => data.appointments
})

export const useFormKeys = () => useQuery({
  queryKey: keys.formKeys(),
  queryFn: api.getFormKeys,
  select: data => data.form_keys
})

/* ==========================================================================
   Mutations — each declares everything it invalidates.
   ========================================================================== */

/**
 * Move a job to another stage.
 *
 * Invalidates three things, and all three are load-bearing:
 *   · jobs   — the card has to move column on the board;
 *   · job    — the detail screen's own stage label;
 *   · job    — stage_history is nested inside the job detail response, so the
 *              timeline entry this move just created arrives with it.
 * The history is the one people forget, because it is not obviously "the job".
 */
export function useMoveStage () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, stageId, reason }) => api.moveJobStage(jobId, stageId, reason),
    onSuccess: (_data, { jobId }) => {
      qc.invalidateQueries({ queryKey: keys.jobs() })
      qc.invalidateQueries({ queryKey: keys.job(jobId) })
    }
  })
}

export function useCreateJob () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.createJob,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: keys.jobs() })
      // The client's detail screen lists their jobs.
      if (data?.job?.client_id) {
        qc.invalidateQueries({ queryKey: keys.client(data.job.client_id) })
      }
    }
  })
}

export function useUpdateJob () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }) => api.updateJob(id, body),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: keys.jobs() })
      qc.invalidateQueries({ queryKey: keys.job(id) })
    }
  })
}

export function useCreateClient () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.createClient,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.clients() })
  })
}

export function useUpdateClient () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }) => api.updateClient(id, body),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: keys.clients() })
      qc.invalidateQueries({ queryKey: keys.client(id) })
      // Board cards carry the client's name, so a rename has to reach them.
      qc.invalidateQueries({ queryKey: keys.jobs() })
    }
  })
}

/**
 * Estimate mutations all invalidate the parent job, because the job detail
 * screen lists its estimates with their statuses — a sent estimate that still
 * reads "draft" one screen up is the kind of inconsistency that makes a user
 * stop trusting what they are looking at.
 */
export function useSaveEstimate () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }) => (id ? api.updateEstimate(id, body) : api.createEstimate(body)),
    onSuccess: data => {
      const est = data.estimate
      qc.invalidateQueries({ queryKey: keys.estimate(est.id) })
      qc.invalidateQueries({ queryKey: keys.estimates() })
      qc.invalidateQueries({ queryKey: keys.job(est.job_id) })
    }
  })
}

export function useEstimateAction (action) {
  const qc = useQueryClient()
  const fn = {
    send: ({ id }) => api.sendEstimate(id),
    accept: ({ id, acceptedByName }) => api.acceptEstimate(id, acceptedByName),
    decline: ({ id }) => api.declineEstimate(id)
  }[action]

  return useMutation({
    mutationFn: fn,
    onSuccess: data => {
      const est = data.estimate
      qc.invalidateQueries({ queryKey: keys.estimate(est.id) })
      qc.invalidateQueries({ queryKey: keys.estimates() })
      qc.invalidateQueries({ queryKey: keys.job(est.job_id) })
      // Accepting unlocks invoicing, so anything showing "can this be
      // invoiced?" has to re-evaluate.
      qc.invalidateQueries({ queryKey: keys.invoices() })
    }
  })
}

export function useCreateInvoice () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.createInvoice,
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: keys.invoices() })
      if (data?.invoice?.job_id) qc.invalidateQueries({ queryKey: keys.job(data.invoice.job_id) })
    }
  })
}

export function useInvoiceAction (action) {
  const qc = useQueryClient()
  const fn = {
    send: ({ id }) => api.sendInvoice(id),
    status: ({ id, status }) => api.setInvoiceStatus(id, status)
  }[action]

  return useMutation({
    mutationFn: fn,
    onSuccess: data => {
      const inv = data.invoice
      qc.invalidateQueries({ queryKey: keys.invoice(inv.id) })
      qc.invalidateQueries({ queryKey: keys.invoices() })
      qc.invalidateQueries({ queryKey: keys.job(inv.job_id) })
    }
  })
}

/* --------------------------------------------------------------------------
   Payments (Phase 2 items 11 + 13)

   ── WHY EVERY MONEY MUTATION INVALIDATES FOUR THINGS ─────────────────────
   Recording a payment changes the invoice's own money columns, its payment
   history, the invoice list's outstanding total, and the parent job's invoice
   summary. Three of those are on screens the user may be looking at right now.
   A contractor who records a $400 cheque and still sees "balance $1,000" one
   screen up does not think "stale cache" — they think the payment did not
   save, and they record it again. On this surface that is a duplicated
   financial record, which is exactly what the idempotency key exists to stop
   and exactly the kind of thing a UI should not be provoking in the first
   place.
   -------------------------------------------------------------------------- */

export const useInvoicePayments = id => useQuery({
  queryKey: keys.invoicePayments(id),
  queryFn: () => api.getInvoicePayments(id),
  enabled: Boolean(id)
})

export const useInvoiceAmendments = id => useQuery({
  queryKey: keys.invoiceAmendments(id),
  queryFn: () => api.getInvoiceAmendments(id),
  enabled: Boolean(id),
  select: data => data.amendments
})

export const useBalanceInvoicePreview = jobId => useQuery({
  queryKey: keys.balanceInvoice(jobId),
  queryFn: () => api.previewBalanceInvoice(jobId),
  enabled: Boolean(jobId)
})

function invalidateMoney (qc, { invoiceId, jobId }) {
  qc.invalidateQueries({ queryKey: keys.invoice(invoiceId) })
  qc.invalidateQueries({ queryKey: keys.invoicePayments(invoiceId) })
  qc.invalidateQueries({ queryKey: keys.invoices() })
  if (jobId) {
    qc.invalidateQueries({ queryKey: keys.job(jobId) })
    qc.invalidateQueries({ queryKey: keys.balanceInvoice(jobId) })
  }
}

export function useRecordPayment () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, ...body }) => api.recordPayment(invoiceId, body),
    onSuccess: data => invalidateMoney(qc, {
      invoiceId: data.invoice.id, jobId: data.invoice.job_id
    })
  })
}

export function useReversePayment () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentId, ...body }) => api.reversePayment(paymentId, body),
    onSuccess: data => invalidateMoney(qc, {
      invoiceId: data.invoice.id, jobId: data.invoice.job_id
    })
  })
}

export function useCreateBalanceInvoice () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, ...body }) => api.createBalanceInvoice(jobId, body),
    onSuccess: (data, { jobId }) => {
      qc.invalidateQueries({ queryKey: keys.invoices() })
      qc.invalidateQueries({ queryKey: keys.job(jobId) })
      qc.invalidateQueries({ queryKey: keys.balanceInvoice(jobId) })
    }
  })
}

export function useSaveAppointment () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }) => (id ? api.updateAppointment(id, body) : api.createAppointment(body)),
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: keys.appointments() })
      const jobId = data?.appointment?.job_id
      if (jobId) qc.invalidateQueries({ queryKey: keys.job(jobId) })
    }
  })
}

export function useCrewAssignment () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, userId, roleOnJob, remove }) =>
      (remove ? api.unassignCrew(jobId, userId) : api.assignCrew(jobId, userId, roleOnJob)),
    onSuccess: (_d, { jobId }) => qc.invalidateQueries({ queryKey: keys.job(jobId) })
  })
}

export function useUpdateFormKeyOrigins () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, allowedOrigins }) => api.updateFormKeyOrigins(id, allowedOrigins),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.formKeys() })
  })
}

export function useInviteUser () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.inviteUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.users() })
      qc.invalidateQueries({ queryKey: keys.userSeats() })
    }
  })
}

/* ==========================================================================
   Tenant settings (Phase 2 item 16a — §5 items 6a + 6c)
   ========================================================================== */

export const useSettings = () => useQuery({
  queryKey: keys.settings(),
  queryFn: api.getSettings,
  // Tenant configuration, edited by hand on this screen and nowhere else.
  staleTime: 5 * 60 * 1000
})

/**
 * Both settings mutations write the fresh row straight into the cache rather
 * than invalidating it.
 *
 * The endpoint returns the complete post-write state, so a refetch would ask
 * the server a question it just answered — and on a form with a live preview
 * that round trip is visible as the preview flickering back to the old value
 * before settling on the new one.
 */
export function useUpdateDepositSettings () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.updateDepositSettings,
    onSuccess: data => qc.setQueryData(keys.settings(), data)
  })
}

export function useUpdatePublicProfile () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.updatePublicProfile,
    onSuccess: data => qc.setQueryData(keys.settings(), data)
  })
}

/* ==========================================================================
   Payments boarding (Phase 2 §4.2)
   ========================================================================== */

export const useOnboardingStatus = () => useQuery({
  queryKey: keys.onboarding(),
  queryFn: api.getOnboardingStatus,
  // This read calls Maverick for the outstanding-requirements half, so it is
  // not free. A contractor watching for approval reloads; the app does not
  // poll on their behalf, because underwriting takes days, not seconds.
  staleTime: 60 * 1000,
  retry: false
})

export function useStartOnboarding () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.startOnboarding,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.onboarding() })
      // Boarding flips tenants.maverick_status to 'pending', which the shell
      // reads for the payments gate. Without this the Settings card updates
      // and the rest of the app still believes payments were never started.
      qc.invalidateQueries({ queryKey: keys.me() })
    }
  })
}

/* ==========================================================================
   Share links (Phase 2 items 6 + 7, surfaced by 16a)

   Issuing or revoking a link changes the ESTIMATE screen too — signing
   revokes every outstanding token for that estimate (D17), so a stale list
   would show live links for a document that can no longer be signed.
   ========================================================================== */

export const useShareLinks = estimateId => useQuery({
  queryKey: keys.shareLinks(estimateId),
  queryFn: () => api.getShareLinks(estimateId),
  enabled: Boolean(estimateId),
  select: data => data.share_links
})

export function useCreateShareLink () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ estimateId, captureMode }) => api.createShareLink(estimateId, captureMode),
    onSuccess: (_data, { estimateId }) =>
      qc.invalidateQueries({ queryKey: keys.shareLinks(estimateId) })
  })
}

export function useRevokeShareLink () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ estimateId, linkId }) => api.revokeShareLink(estimateId, linkId),
    onSuccess: (_data, { estimateId }) =>
      qc.invalidateQueries({ queryKey: keys.shareLinks(estimateId) })
  })
}

/**
 * Mint a signed PDF URL. A MUTATION rather than a query, deliberately: the URL
 * expires (D18), so caching one is caching something that stops working. Call
 * it at the moment of the click and navigate straight to the result.
 */
export function useSignedPdfUrl () {
  return useMutation({ mutationFn: ({ estimateId }) => api.getSignedPdfUrl(estimateId) })
}

/* ==========================================================================
   Document delivery (Phase 2 item 17)
   ========================================================================== */

export const useEstimateDeliveries = estimateId => useQuery({
  queryKey: keys.estimateDeliveries(estimateId),
  queryFn: () => api.getEstimateDeliveries(estimateId),
  enabled: Boolean(estimateId),
  select: data => data.deliveries
})

export const useInvoiceDeliveries = invoiceId => useQuery({
  queryKey: keys.invoiceDeliveries(invoiceId),
  queryFn: () => api.getInvoiceDeliveries(invoiceId),
  enabled: Boolean(invoiceId),
  select: data => data.deliveries
})

export function useSendEstimateEmail () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ estimateId, ...body }) => api.sendEstimateEmail(estimateId, body),
    onSuccess: (_data, { estimateId }) => {
      qc.invalidateQueries({ queryKey: keys.estimateDeliveries(estimateId) })
      // A fresh share link was issued alongside the send.
      qc.invalidateQueries({ queryKey: keys.shareLinks(estimateId) })
    }
  })
}

export function useSendEstimateSms () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ estimateId, ...body }) => api.sendEstimateSms(estimateId, body),
    onSuccess: (_data, { estimateId }) => {
      qc.invalidateQueries({ queryKey: keys.estimateDeliveries(estimateId) })
      qc.invalidateQueries({ queryKey: keys.shareLinks(estimateId) })
    }
  })
}

export function useSendInvoiceEmail () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, ...body }) => api.sendInvoiceEmail(invoiceId, body),
    onSuccess: (_data, { invoiceId }) =>
      qc.invalidateQueries({ queryKey: keys.invoiceDeliveries(invoiceId) })
  })
}

export function useSendInvoiceSms () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, ...body }) => api.sendInvoiceSms(invoiceId, body),
    onSuccess: (_data, { invoiceId }) =>
      qc.invalidateQueries({ queryKey: keys.invoiceDeliveries(invoiceId) })
  })
}

/* --------------------------------------------------------------------------
   Sending-domain verification (item 17)
   -------------------------------------------------------------------------- */

export const useSendingDomainStatus = () => useQuery({
  queryKey: keys.sendingDomain(),
  queryFn: api.getSendingDomainStatus,
  staleTime: 30 * 1000
})

export function useCreateSendingDomain () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.createSendingDomain,
    onSuccess: data => {
      qc.setQueryData(keys.sendingDomain(), data)
      qc.invalidateQueries({ queryKey: keys.settings() })
    }
  })
}

export function useVerifySendingDomain () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.verifySendingDomain,
    onSuccess: data => {
      qc.setQueryData(keys.sendingDomain(), data)
      qc.invalidateQueries({ queryKey: keys.settings() })
    }
  })
}

/* ==========================================================================
   Notifications (Phase 2 item 19)
   ========================================================================== */

export const useNotifications = () => useQuery({
  queryKey: keys.notifications(),
  queryFn: api.getNotifications,
  // Short stale time, not a websocket subscription this session — the
  // `notifications` table is already wired into supabase_realtime for a
  // future live feed, additive rather than a rewrite when that is built.
  staleTime: 30 * 1000,
  refetchInterval: 60 * 1000
})

export function useMarkNotificationRead () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications() })
  })
}

export function useMarkAllNotificationsRead () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications() })
  })
}

export const useNotificationPreferences = () => useQuery({
  queryKey: keys.notificationPreferences(),
  queryFn: api.getNotificationPreferences,
  select: data => data.preferences,
  staleTime: 5 * 60 * 1000
})

export function useUpdateNotificationPreference () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ type, ...body }) => api.updateNotificationPreference(type, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notificationPreferences() })
  })
}

/* ==========================================================================
   Data export (Phase 2 item 21)
   ========================================================================== */

export const useDataExports = () => useQuery({
  queryKey: keys.dataExports(),
  queryFn: api.getDataExports,
  select: data => data.data_exports
})

export function useCreateDataExport () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.createDataExport,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.dataExports() })
  })
}

/** A mutation, not a query — the URL expires (D18-style), same reasoning as useSignedPdfUrl. */
export function useDataExportDownloadUrl () {
  return useMutation({ mutationFn: api.getDataExportDownloadUrl })
}

/* ==========================================================================
   The Operations zone (Phase 9 Session A)
   ========================================================================== */

export const useOperationsOverview = () => useQuery({
  queryKey: keys.operationsOverview(),
  queryFn: api.getOperationsOverview,
  select: data => data.operations,
  // Which sections are live is a property of the deployed build, so it changes
  // on release rather than during a session.
  staleTime: 5 * 60 * 1000,
  // A 403 here means the grant was revoked while this tab was open. That is a
  // designed outcome of the per-request read, not a transient fault, so
  // retrying it just delays the explanation.
  retry: false
})

export const useOperationsReport = (filters = {}) => useQuery({
  queryKey: keys.operationsReport(filters),
  queryFn: () => api.getOperationsReport(filters),
  select: data => data.report,
  retry: false
})

export const useDocumentTemplates = () => useQuery({
  queryKey: keys.documentTemplates(),
  queryFn: api.getDocumentTemplates,
  retry: false
})

export function useUpdateDocumentTemplates () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.updateDocumentTemplates,
    onSuccess: data => {
      qc.setQueryData(keys.documentTemplates(), data)
      // The shell reads branding from `me`; refresh it so later real document
      // actions in this session use the just-saved preferences too.
      qc.invalidateQueries({ queryKey: keys.me() })
      qc.invalidateQueries({ queryKey: keys.operationsOverview() })
    }
  })
}

export const usePreviewDocumentTemplates = () => useMutation({
  mutationFn: ({ documentType, documentTemplates }) =>
    api.previewDocumentTemplates(documentType, documentTemplates)
})

/**
 * Grant or revoke Operations access for a teammate (owner only).
 *
 * Invalidates `me` as well as `users`, and that is load-bearing rather than
 * defensive: an owner can be looking at their own row, and more importantly
 * the shell's navigation is drawn from `me`. Without it, a change made here
 * would be correct on the server and invisible in the rail until a reload.
 */
export function useSetOperationsAccess () {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, operationsAccess }) =>
      api.setUserOperationsAccess(userId, operationsAccess),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.users() })
      qc.invalidateQueries({ queryKey: keys.me() })
    }
  })
}
